import React, { useState, useEffect } from 'react';
import { getJobs, cancelJob } from '../services/api';
// import useWebSocket from '../hooks/useWebSocket';
import { useWebSocket } from '../hooks/useWebSocket';
import CancelJobModal from '../components/CancelJobModal';
import { FiRefreshCw, FiCheckCircle, FiXCircle, FiX } from 'react-icons/fi';
import '../styles/JobsPage.css';

function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useWebSocket((message) => {
    if (message.type === 'job_update') {
      setJobs(prevJobs => {
        const index = prevJobs.findIndex(j => j._id === message.jobId);
        if (index >= 0) {
          const updated = [...prevJobs];
          updated[index] = {
            ...updated[index],
            status: message.status,
            progress: message.progress,
            current_step: message.currentStep,
            logs: message.logs
          };
          return updated;
        }
        return prevJobs;
      });
    }
  });

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const response = await getJobs();
      setJobs(response.data.data || []);
    } catch (error) {
      console.error('Failed to load jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelJob = (job) => {
    setSelectedJob(job);
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async (cancelType) => {
    try {
      await cancelJob(selectedJob._id, cancelType);
      setShowCancelModal(false);
      await loadJobs();
    } catch (error) {
      console.error('Failed to cancel job:', error);
      alert('Failed to cancel job');
    }
  };

  // Helper to format milliseconds into readable time
  const formatDuration = (start, end) => {
    const diff = new Date(end) - new Date(start);
    const seconds = Math.floor((diff / 1000) % 60);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const hours = Math.floor(diff / (1000 * 60 * 60));

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

    return parts.join(' ');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
        return <FiRefreshCw className="status-icon running" />;
      case 'completed':
        return <FiCheckCircle className="status-icon completed" />;
      case 'failed':
      case 'cancelled':
        return <FiXCircle className="status-icon failed" />;
      default:
        return null;
    }
  };

  if (loading) {
    return <div className="loading">Loading jobs...</div>;
  }

  return (
    <div className="jobs-page">
      <div className="page-header">
        <h2>Jobs & Activity</h2>
        <button className="btn btn-secondary" onClick={loadJobs}>
          <FiRefreshCw /> Refresh
        </button>
      </div>

      <div className="jobs-list">
        {jobs.length === 0 ? (
          <div className="empty-state">
            <p>No jobs yet. Trigger a backup or deployment to see jobs here.</p>
          </div>
        ) : (
          jobs.map(job => (
            <div key={job._id} className={`job-card status-${job.status}`}>
              <div className="job-header">
                <div className="job-info">
                  {getStatusIcon(job.status)}
                  <div>
                    <h3>{job.type.toUpperCase()} - {job.target_type}</h3>
                    <span className="job-meta">
                      {job.completed_at ? (
                        // Show only duration if completed
                        `Duration: ${formatDuration(job.createdAt, job.completed_at)}`
                      ) : (
                        // Show start time if still in progress or pending
                        new Date(job.createdAt).toLocaleString()
                      )}
                    </span>
                  </div>
                </div>
                {job.status === 'running' && (
                  <button 
                    className="btn btn-sm btn-danger" 
                    onClick={() => handleCancelJob(job)}
                  >
                    <FiX /> Cancel
                  </button>
                )}
              </div>

              {job.status === 'running' && (
                <div className="job-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${job.progress}%` }}
                    />
                  </div>
                  <div className="progress-text">
                    {job.progress}% - {job.current_step}
                  </div>
                </div>
              )}

              {job.error && (
                <div className="job-error">
                  <strong>Error:</strong> {job.error}
                </div>
              )}

              {job.logs && job.logs.length > 0 && (
                <div className="job-logs">
                  <h4>Logs</h4>
                  <div className="logs-content">
                    {job.logs.map((log, idx) => (
                      <div key={idx} className="log-entry">
                        <span className="log-step">[{log.step}]</span>
                        <span className="log-message">{log.message}</span>
                        <span className="log-time">
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {showCancelModal && selectedJob && (
        <CancelJobModal
          job={selectedJob}
          onConfirm={handleCancelConfirm}
          onClose={() => setShowCancelModal(false)}
        />
      )}
    </div>
  );
}

export default JobsPage;
